/**
 * Conditions Portal Main Component
 *
 * Token-based portal for viewing loan conditions. Accessed by processors,
 * loan officers, borrowers, or agents via forwarded link.
 *
 * Features:
 * - Token-based authentication (no login required)
 * - Approval letter PDF download
 * - Condition template downloads (where available)
 * - Document upload for addressing conditions
 * - Mobile responsive design matching internal app
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Download, RefreshCw, AlertCircle, Clock, CheckCircle2, Circle, Phone, Mail, User, Users, Send, FileText, Loader2, Trash2, Building, ChevronDown, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { TitleEscrowConditionsSection } from './TitleEscrowConditionsSection';
import { BorrowerConditionsSection } from './BorrowerConditionsSection';
import { ConditionUploadModal } from './ConditionUploadModal';
import { TitleEscrowAssignModal } from './TitleEscrowAssignModal';
import { BulkReassignModal } from './BulkReassignModal';
import { PortalHeader } from './PortalHeader';
import { LoanSummaryCard } from './LoanSummaryCard';
import { ContactCards } from './ContactCards';
import { PortalHelpSection } from './PortalHelpSection';
import { PortalFooter } from './PortalFooter';
import { PortalDocumentsTab } from './PortalDocumentsTab';
import type {
  ConditionsPortalData,
  ConditionsPortalUpload,
  ConditionTemplateInfo,
  LoanTeamContact,
  TitleEscrowInfo,
  PortalDocument
} from '@/types/conditions-portal';
import type { EncompassCondition } from '@/types/approval-letter';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ConditionsPortal', 'conditions-portal');

// API configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface PortalState {
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  data: ConditionsPortalData | null;
  lastRefresh: Date | null;
}

export const ConditionsPortal = () => {
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();

  const [state, setState] = useState<PortalState>({
    loading: true,
    error: null,
    errorCode: null,
    data: null,
    lastRefresh: null
  });

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<EncompassCondition | null>(null);
  const [showOnlyOpen, setShowOnlyOpen] = useState(true); // Default to showing only open conditions
  const [timingFilter, setTimingFilter] = useState<'all' | 'ptd' | 'ptf' | 'ac'>('all'); // Filter by timing
  const [ownerFilter, setOwnerFilter] = useState<string>('all'); // Filter by condition owner
  const [submittingToProcessing, setSubmittingToProcessing] = useState(false);
  const [hasNewData, setHasNewData] = useState(false); // Track when background refresh found changes
  const [pendingData, setPendingData] = useState<ConditionsPortalData | null>(null); // Store new data until user refreshes
  const [titleEscrowModalOpen, setTitleEscrowModalOpen] = useState(false); // Title/Escrow assignment modal
  const [bulkReassignModalOpen, setBulkReassignModalOpen] = useState(false); // Bulk condition reassignment modal
  const [activeTab, setActiveTab] = useState<'conditions' | 'documents'>('conditions'); // Tab for title/escrow portal

  /**
   * Validate token and fetch portal data
   */
  const fetchPortalData = useCallback(async () => {
    if (!token) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Invalid link. Please check the URL and try again.',
        errorCode: 'INVALID_TOKEN'
      }));
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'validate', token })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        log.warn('Portal validation failed', { code: result.code });
        setState(prev => ({
          ...prev,
          loading: false,
          error: result.error || 'Failed to load portal data',
          errorCode: result.code || 'SERVER_ERROR'
        }));
        return;
      }

      log.info('Portal data loaded successfully');

      setState({
        loading: false,
        error: null,
        errorCode: null,
        data: result.data,
        lastRefresh: new Date()
      });

    } catch (error) {
      log.error('Portal fetch error', { error });
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Unable to connect. Please check your internet connection.',
        errorCode: 'SERVER_ERROR'
      }));
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    fetchPortalData();
  }, [fetchPortalData]);

  // Silent background refresh every 60 seconds - only notify user if data changed
  useEffect(() => {
    if (!state.data) {
return;
}

    const checkForUpdates = async () => {
      try {
        const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: SUPABASE_ANON_KEY
          },
          body: JSON.stringify({ action: 'validate', token })
        });

        const result = await response.json();
        if (!response.ok || !result.success) {
return;
}

        // Compare conditions to see if anything changed
        const currentConditions = state.data?.loanData?.conditions;
        const newConditions = result.data?.loanData?.conditions;

        const currentAll = [
          ...(currentConditions?.ptd || []),
          ...(currentConditions?.ptf || []),
          ...(currentConditions?.ac || [])
        ];
        const newAll = [
          ...(newConditions?.ptd || []),
          ...(newConditions?.ptf || []),
          ...(newConditions?.ac || [])
        ];

        const currentCleared = currentAll.filter(c => !c.statusOpen).length;
        const newCleared = newAll.filter(c => !c.statusOpen).length;

        // Check if counts changed or if any status changed
        if (currentAll.length !== newAll.length || currentCleared !== newCleared) {
          log.info('Background refresh detected changes', {
            oldCount: currentAll.length,
            newCount: newAll.length,
            oldCleared: currentCleared,
            newCleared
          });
          setPendingData(result.data);
          setHasNewData(true);
        }

        // Silently update lock status without triggering full refresh banner
        const currentLocked = state.data?.loanData?.isFileLocked;
        const newLocked = result.data?.loanData?.isFileLocked;
        if (currentLocked !== newLocked) {
          setState(prev => prev.data ? {
            ...prev,
            data: {
              ...prev.data,
              loanData: {
                ...prev.data.loanData,
                isFileLocked: result.data?.loanData?.isFileLocked ?? false,
                fileLockedByName: result.data?.loanData?.fileLockedByName ?? null,
                fileLockTime: result.data?.loanData?.fileLockTime ?? null
              }
            }
          } : prev);
        }
      } catch (error) {
        // Silent fail - don't disrupt user experience
        log.warn('Background refresh failed silently', { error });
      }
    };

    const interval = setInterval(checkForUpdates, 60000);

    return () => clearInterval(interval);
  }, [state.data, token]);

  /**
   * Apply pending data when user clicks refresh banner
   */
  const applyPendingData = useCallback(() => {
    if (pendingData) {
      setState({
        loading: false,
        error: null,
        errorCode: null,
        data: pendingData,
        lastRefresh: new Date()
      });
      setPendingData(null);
      setHasNewData(false);
      toast({
        title: 'Updated',
        description: 'Conditions have been refreshed.'
      });
    }
  }, [pendingData, toast]);

  /**
   * Download approval letter PDF
   */
  const handleDownloadPdf = async () => {
    if (!token || downloadingPdf) {
return;
}

    try {
      setDownloadingPdf(true);

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'get-pdf', token })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to generate PDF');
      }

      // Create blob and download
      const byteCharacters = atob(result.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename || 'approval-letter.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Download started',
        description: 'Your approval letter PDF is downloading.'
      });

    } catch (error) {
      log.error('PDF download error', { error });
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Unable to download PDF',
        variant: 'destructive'
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  /**
   * Download condition template document
   */
  const handleDownloadConditionDoc = async (externalId: string, templateName: string, borrowerPairId?: string) => {
    if (!token) {
return;
}

    try {
      const requestBody: Record<string, string> = { action: 'get-condition-doc', token, externalId };
      if (borrowerPairId) {
        requestBody.borrowerPairId = borrowerPairId;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to get document');
      }

      // Open signed URL in new tab (noopener,noreferrer prevents reverse tabnabbing)
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');

      toast({
        title: 'Download started',
        description: `Downloading ${templateName}`
      });

    } catch (error) {
      log.error('Condition doc download error', { externalId, error });
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Unable to download document',
        variant: 'destructive'
      });
    }
  };

  /**
   * Open upload modal for a condition
   */
  const handleUploadClick = (condition: EncompassCondition) => {
    setSelectedCondition(condition);
    setUploadModalOpen(true);
  };

  /**
   * Handle successful upload
   */
  const handleUploadSuccess = (uploadId: string, confirmationNumber: string) => {
    toast({
      title: 'Document uploaded',
      description: `Confirmation: ${confirmationNumber}. Click "Submit to Processing" when ready.`
    });
    setUploadModalOpen(false);
    setSelectedCondition(null);
    // Refresh data to show new upload
    fetchPortalData();
  };

  /**
   * Submit pending uploads to Encompass and notify processing team
   */
  const handleSubmitToProcessing = async () => {
    if (!token || submittingToProcessing) {
return;
}

    try {
      setSubmittingToProcessing(true);

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'submit-to-processing', token })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to submit documents');
      }

      toast({
        title: 'Documents submitted!',
        description: result.message
      });

      // Refresh data to show updated statuses
      fetchPortalData();

    } catch (error) {
      log.error('Submit to processing error', { error });
      toast({
        title: 'Submission failed',
        description: error instanceof Error ? error.message : 'Unable to submit documents',
        variant: 'destructive'
      });
    } finally {
      setSubmittingToProcessing(false);
    }
  };

  // Error states
  if (state.error) {
    return (
      <PortalErrorState
        error={state.error}
        errorCode={state.errorCode}
        onRetry={state.errorCode === 'SERVER_ERROR' ? fetchPortalData : undefined}
      />
    );
  }

  // Loading state
  if (state.loading || !state.data) {
    return <PortalLoadingState />;
  }

  const { loanData, loanOfficer, company, templates, uploads, conditionNotes, additionalCcRecipients } = state.data;
  const allConditions = [...loanData.conditions.ptd, ...loanData.conditions.ptf, ...loanData.conditions.ac];
  const openConditions = allConditions.filter(c => c.statusOpen);
  const clearedConditions = allConditions.filter(c => !c.statusOpen);
  const isTitleEscrow = state.data?.meta.recipientType === 'title_escrow';

  // Extract unique owners from all conditions for filter dropdown
  const uniqueOwners = Array.from(
    new Set(allConditions.map(c => c.owner?.entityName || 'Unassigned'))
  ).sort();

  // Helper to filter conditions by owner
  const filterByOwner = (conditions: EncompassCondition[]) => {
    if (ownerFilter === 'all') {
return conditions;
}
    return conditions.filter(c => (c.owner?.entityName || 'Unassigned') === ownerFilter);
  };

  // Professional layout for Title/Escrow recipients
  if (isTitleEscrow) {
    return (
      <div className="min-h-screen bg-gray-50 overflow-auto">
        {/* Professional Header */}
        <PortalHeader company={company} />

        {/* File Lock Warning Banner */}
        {loanData.isFileLocked && (
          <div className="bg-amber-50 border-b border-amber-200">
            <div className="max-w-4xl mx-auto px-4 py-3">
              <div className="flex items-center gap-2 text-amber-800">
                <Lock className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium">
                  {loanData.fileLockedByName || 'Someone'} has this loan open in Encompass. Uploads may be delayed until they close the file.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="py-6 pb-24">
          <div className="max-w-4xl mx-auto px-4 space-y-6">
            {/* Loan Summary with Urgency */}
            <LoanSummaryCard loanData={loanData} documentsNeededBy={isTitleEscrow ? state.data.documentsNeededBy : undefined} />

            {/* Contact Cards */}
            <ContactCards loanOfficer={loanOfficer} processor={loanData.processor} />

            {/* Tab Navigation (if documents are attached) */}
            {state.data?.attachedDocuments && state.data.attachedDocuments.length > 0 && (
              <div className="flex border-b border-gray-200 bg-white rounded-t-lg">
                <button
                  className={`px-6 py-3 font-medium text-sm transition-colors ${
                    activeTab === 'conditions'
                      ? 'border-b-2 border-[#183C2E] text-[#183C2E] bg-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  onClick={() => setActiveTab('conditions')}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Conditions ({openConditions.length})
                  </div>
                </button>
                <button
                  className={`px-6 py-3 font-medium text-sm transition-colors ${
                    activeTab === 'documents'
                      ? 'border-b-2 border-[#183C2E] text-[#183C2E] bg-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  onClick={() => setActiveTab('documents')}
                >
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Documents ({state.data.attachedDocuments.length})
                  </div>
                </button>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && state.data?.attachedDocuments && state.data.attachedDocuments.length > 0 && (
              <PortalDocumentsTab
                documents={state.data.attachedDocuments}
                token={token || ''}
              />
            )}

            {/* Conditions Tab */}
            {activeTab === 'conditions' && (
            <Card className="border-[#183C2E]">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#183C2E]" />
                  Your Assigned Conditions ({openConditions.length} open)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* PTD - Prior to Docs */}
                {(() => {
                  const ptdConditions = loanData.conditions.ptd.filter(c => c.statusOpen);
                  return ptdConditions.length > 0 ? (
                    <TitleEscrowConditionsSection
                      title="Prior to Docs"
                      conditions={ptdConditions}
                      templates={templates}
                      uploads={uploads}
                      notes={conditionNotes}
                      onDownloadTemplate={handleDownloadConditionDoc}
                      onUploadClick={handleUploadClick}
                      borrowerPairs={loanData.borrowerPairs}
                    />
                  ) : null;
                })()}

                {/* PTF - Prior to Funding */}
                {(() => {
                  const ptfConditions = loanData.conditions.ptf.filter(c => c.statusOpen);
                  return ptfConditions.length > 0 ? (
                    <TitleEscrowConditionsSection
                      title="Prior to Funding"
                      conditions={ptfConditions}
                      templates={templates}
                      uploads={uploads}
                      notes={conditionNotes}
                      onDownloadTemplate={handleDownloadConditionDoc}
                      onUploadClick={handleUploadClick}
                      borrowerPairs={loanData.borrowerPairs}
                    />
                  ) : null;
                })()}

                {/* AC - At Closing */}
                {(() => {
                  const acConditions = loanData.conditions.ac.filter(c => c.statusOpen);
                  return acConditions.length > 0 ? (
                    <TitleEscrowConditionsSection
                      title="At Closing"
                      conditions={acConditions}
                      templates={templates}
                      uploads={uploads}
                      notes={conditionNotes}
                      onDownloadTemplate={handleDownloadConditionDoc}
                      onUploadClick={handleUploadClick}
                      borrowerPairs={loanData.borrowerPairs}
                    />
                  ) : null;
                })()}

                {/* All cleared */}
                {openConditions.length === 0 && (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <p className="text-green-700 font-medium">All conditions have been cleared!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Thank you for your prompt response.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Pending Documents - Ready to Submit (only show on conditions tab) */}
            {activeTab === 'conditions' && (() => {
              const pendingUploads = uploads.filter(u => u.encompassSyncStatus === 'pending');
              const syncedUploads = uploads.filter(u => u.encompassSyncStatus === 'synced');

              if (pendingUploads.length === 0 && syncedUploads.length === 0) {
return null;
}

              return (
                <Card className={pendingUploads.length > 0 ? 'border-amber-300 bg-amber-50/50' : 'border-[#183C2E]'}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Your Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Pending uploads */}
                    {pendingUploads.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                          Ready to Submit ({pendingUploads.length})
                        </p>
                        {pendingUploads.map((upload) => (
                          <div
                            key={upload.id}
                            className="flex items-center gap-2 p-2 bg-white rounded border border-amber-200"
                          >
                            <FileText className="h-4 w-4 text-amber-600 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{upload.originalFilename}</p>
                              <p className="text-xs text-muted-foreground">{upload.conditionName}</p>
                            </div>
                            <Clock className="h-3 w-3 text-amber-500" />
                          </div>
                        ))}

                        {/* Submit Button */}
                        <Button
                          className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                          onClick={handleSubmitToProcessing}
                          disabled={submittingToProcessing}
                        >
                          {submittingToProcessing ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Submit to Processing
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Already synced uploads */}
                    {syncedUploads.length > 0 && (
                      <div className="space-y-2">
                        {pendingUploads.length > 0 && <div className="border-t pt-3" />}
                        <p className="text-xs font-medium text-green-700 uppercase tracking-wide">
                          Submitted ({syncedUploads.length})
                        </p>
                        {syncedUploads.map((upload) => (
                          <div
                            key={upload.id}
                            className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200"
                          >
                            <FileText className="h-4 w-4 text-green-600 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{upload.originalFilename}</p>
                              <p className="text-xs text-muted-foreground">{upload.conditionName}</p>
                            </div>
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Help Section - show on both tabs */}
            <PortalHelpSection />
          </div>
        </main>

        {/* Professional Footer */}
        <PortalFooter company={company} />

        {/* Upload Modal */}
        {selectedCondition && (
          <ConditionUploadModal
            open={uploadModalOpen}
            onOpenChange={setUploadModalOpen}
            condition={selectedCondition}
            token={token || ''}
            onSuccess={handleUploadSuccess}
            isFileLocked={loanData.isFileLocked}
            fileLockedByName={loanData.fileLockedByName}
          />
        )}
      </div>
    );
  }

  // Original layout for other recipient types (borrower, processor, etc.)
  return (
    <div className="h-screen overflow-y-auto bg-background flex flex-col">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 md:px-8">
          <div className="relative flex items-center justify-between">
            {/* Left - Loan Info */}
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold">Loan Conditions</h1>
              <span className="text-muted-foreground">•</span>
              <p className="text-sm text-muted-foreground">Loan #{loanData.loanNumber}</p>
            </div>

            {/* Center - Company Name */}
            <div className="absolute left-1/2 -translate-x-1/2 hidden lg:block">
              <span className="text-2xl font-bold bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] bg-clip-text text-transparent tracking-tight">
                Lendwise Mortgage Inc.
              </span>
            </div>

            {/* Right - Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Assignment controls are internal-only (InternalConditionsPortal).
                 All token-based recipients use read + upload actions only. */}
            </div>
          </div>
        </div>
      </div>

      {/* New Data Available Banner */}
      {hasNewData && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 py-3 md:px-8">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-blue-800">
                <RefreshCw className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Conditions have been updated.
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
                onClick={applyPendingData}
              >
                <RefreshCw className="h-3 w-3 mr-1.5" />
                Refresh for latest
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* File Lock Warning Banner */}
      {loanData.isFileLocked && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 py-3 md:px-8">
            <div className="flex items-center gap-2 text-amber-800">
              <Lock className="h-4 w-4 shrink-0" />
              <span className="text-sm font-medium">
                {loanData.fileLockedByName || 'Someone'} has this loan open in Encompass. Reassignments made here are queued and apply automatically on unlock; uploads may be delayed.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Top Row - Loan Info and Loss Payee Side by Side */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Loan Info Card */}
            <Card className="border-[#183C2E]">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">
                  Loan #{loanData.loanNumber}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Borrower</p>
                  <p className="font-medium">{loanData.borrower.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Property</p>
                  <p className="font-medium">
                    {loanData.property.address}
                    <br />
                    <span className="text-muted-foreground">
                      {loanData.property.city}, {loanData.property.state} {loanData.property.zip}
                    </span>
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Loan Amount</p>
                    <p className="font-semibold">${loanData.loan.totalLoanAmount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">LTV</p>
                    <p className="font-semibold">{loanData.loan.ltv ? `${loanData.loan.ltv.toFixed(2)}%` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">DTI</p>
                    <p className="font-semibold">{loanData.loan.backRatio ? `${loanData.loan.backRatio.toFixed(2)}%` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rate</p>
                    <p className="font-semibold">{loanData.lock.noteRate ? `${loanData.lock.noteRate.toFixed(3)}%` : '—'}</p>
                  </div>
                </div>
                <Button
                  className="w-full bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] hover:from-[#1F4D3A] hover:to-[#183C2E] text-white"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloadingPdf ? 'Generating PDF...' : 'Download Approval Letter'}
                </Button>
              </CardContent>
            </Card>

            {/* Loss Payee / Mortgagee Clause Card */}
            <Card className="border-[#183C2E]">
              <CardContent className="pt-6">
                <div className="flex items-start gap-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                  <Building className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-blue-800 block text-xs font-semibold uppercase tracking-wide">
                      Loss Payee / Mortgagee Clause
                    </span>
                    <div className="text-sm text-gray-900 mt-1 space-y-0.5">
                      <p className="font-medium">Lendwise Mortgage, Inc. ISAOA</p>
                      <p>21800 Oxnard Street #220</p>
                      <p>Woodland Hills, CA 91367</p>
                      <p className="text-gray-600">Loan #: {loanData.loanNumber}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-1 space-y-6">
              {/* Conditions Progress */}
              <Card className="border-[#183C2E]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold">
                    Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#10B981] transition-all duration-500"
                          style={{
                            width: `${allConditions.length > 0 ? (clearedConditions.length / allConditions.length) * 100 : 0}%`
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-muted-foreground">
                      {clearedConditions.length}/{allConditions.length}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {openConditions.length === 0
                      ? 'All conditions cleared!'
                      : `${openConditions.length} condition${openConditions.length === 1 ? '' : 's'} remaining`}
                  </p>
                </CardContent>
              </Card>

              {/* Pending Documents - Ready to Submit */}
              {(() => {
                const pendingUploads = uploads.filter(u => u.encompassSyncStatus === 'pending');
                const syncedUploads = uploads.filter(u => u.encompassSyncStatus === 'synced');

                if (pendingUploads.length === 0 && syncedUploads.length === 0) {
return null;
}

                return (
                  <Card className={pendingUploads.length > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Your Documents
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Pending uploads */}
                      {pendingUploads.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                            Ready to Submit ({pendingUploads.length})
                          </p>
                          {pendingUploads.map((upload) => (
                            <div
                              key={upload.id}
                              className="flex items-center gap-2 p-2 bg-white rounded border border-amber-200"
                            >
                              <FileText className="h-4 w-4 text-amber-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{upload.originalFilename}</p>
                                <p className="text-xs text-muted-foreground">{upload.conditionName}</p>
                              </div>
                              <Clock className="h-3 w-3 text-amber-500" />
                            </div>
                          ))}

                          {/* Submit Button */}
                          <Button
                            className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                            onClick={handleSubmitToProcessing}
                            disabled={submittingToProcessing}
                          >
                            {submittingToProcessing ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-2" />
                                Submit to Processing
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-center text-muted-foreground">
                            Documents will be sent to Encompass and the processing team
                          </p>
                        </div>
                      )}

                      {/* Already synced uploads */}
                      {syncedUploads.length > 0 && (
                        <div className="space-y-2">
                          {pendingUploads.length > 0 && <div className="border-t pt-3" />}
                          <p className="text-xs font-medium text-green-700 uppercase tracking-wide">
                            Submitted ({syncedUploads.length})
                          </p>
                          {syncedUploads.map((upload) => (
                            <div
                              key={upload.id}
                              className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200"
                            >
                              <FileText className="h-4 w-4 text-green-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{upload.originalFilename}</p>
                                <p className="text-xs text-muted-foreground">{upload.conditionName}</p>
                              </div>
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Loan Team Contacts */}
              {(loanData.processor || loanData.underwriter) && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold">
                      Loan Team
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Processor */}
                    {loanData.processor && (
                      <TeamContactRow
                        label="Processor"
                        contact={loanData.processor}
                      />
                    )}

                    {/* Underwriter */}
                    {loanData.underwriter && (
                      <TeamContactRow
                        label="Underwriter"
                        contact={loanData.underwriter}
                      />
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Title & Escrow Contacts */}
              {loanData.titleEscrow && (loanData.titleEscrow.companyName || loanData.titleEscrow.contactName) && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold">
                      Title & Escrow
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Company Name */}
                    {loanData.titleEscrow.companyName && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium text-sm">{loanData.titleEscrow.companyName}</span>
                        </div>
                        {/* Address if available */}
                        {(loanData.titleEscrow.address || loanData.titleEscrow.city) && (
                          <p className="text-xs text-muted-foreground pl-6">
                            {loanData.titleEscrow.address && <span>{loanData.titleEscrow.address}<br /></span>}
                            {loanData.titleEscrow.city && `${loanData.titleEscrow.city}, `}
                            {loanData.titleEscrow.state} {loanData.titleEscrow.zip}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Escrow Officer Contact */}
                    {loanData.titleEscrow.contactName && (
                      <TitleEscrowContactRow
                        label="Escrow Officer"
                        titleEscrow={loanData.titleEscrow}
                      />
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Conditions */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">
                      Conditions
                    </CardTitle>
                    <div className="flex items-center gap-4">
                      {/* Timing Filter Dropdown */}
                      <Select value={timingFilter} onValueChange={(v) => setTimingFilter(v as typeof timingFilter)}>
                        <SelectTrigger className="h-8 w-[140px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Conditions</SelectItem>
                          <SelectItem value="ptd">Prior to Docs</SelectItem>
                          <SelectItem value="ptf">Prior to Funding</SelectItem>
                          <SelectItem value="ac">At Closing</SelectItem>
                        </SelectContent>
                      </Select>
                      {/* Owner Filter Dropdown */}
                      <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                        <SelectTrigger className="h-8 w-[160px] text-xs">
                          <SelectValue placeholder="All Owners" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Owners</SelectItem>
                          {uniqueOwners.map(owner => (
                            <SelectItem key={owner} value={owner}>{owner}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* Show Only Open Toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          id="show-open-only"
                          checked={showOnlyOpen}
                          onCheckedChange={setShowOnlyOpen}
                        />
                        <Label htmlFor="show-open-only" className="text-xs text-muted-foreground cursor-pointer">
                          Open only
                        </Label>
                      </div>
                      {state.lastRefresh && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Updated {state.lastRefresh.toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* PTD - Prior to Docs */}
                  {(timingFilter === 'all' || timingFilter === 'ptd') && (() => {
                    const filteredPtd = filterByOwner(
                      showOnlyOpen
                        ? loanData.conditions.ptd.filter(c => c.statusOpen)
                        : loanData.conditions.ptd
                    );
                    if (filteredPtd.length > 0) {
                      return (
                        <BorrowerConditionsSection
                          title="Prior to Docs"
                          conditions={filteredPtd}
                          templates={templates}
                          uploads={uploads}
                          notes={conditionNotes}
                          onDownloadTemplate={handleDownloadConditionDoc}
                          onUploadClick={handleUploadClick}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      );
                    } else if (ownerFilter !== 'all') {
                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Circle className="h-5 w-5 text-gray-300" />
                            <h3 className="font-semibold text-gray-400">Prior to Docs</h3>
                          </div>
                          <p className="text-sm text-muted-foreground pl-7">
                            No conditions assigned to {ownerFilter}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* PTF - Prior to Funding */}
                  {(timingFilter === 'all' || timingFilter === 'ptf') && (() => {
                    const filteredPtf = filterByOwner(
                      showOnlyOpen
                        ? loanData.conditions.ptf.filter(c => c.statusOpen)
                        : loanData.conditions.ptf
                    );
                    if (filteredPtf.length > 0) {
                      return (
                        <BorrowerConditionsSection
                          title="Prior to Funding"
                          conditions={filteredPtf}
                          templates={templates}
                          uploads={uploads}
                          notes={conditionNotes}
                          onDownloadTemplate={handleDownloadConditionDoc}
                          onUploadClick={handleUploadClick}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      );
                    } else if (ownerFilter !== 'all') {
                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Circle className="h-5 w-5 text-gray-300" />
                            <h3 className="font-semibold text-gray-400">Prior to Funding</h3>
                          </div>
                          <p className="text-sm text-muted-foreground pl-7">
                            No conditions assigned to {ownerFilter}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* AC - At Closing */}
                  {(timingFilter === 'all' || timingFilter === 'ac') && (() => {
                    const filteredAc = filterByOwner(
                      showOnlyOpen
                        ? loanData.conditions.ac.filter(c => c.statusOpen)
                        : loanData.conditions.ac
                    );
                    if (filteredAc.length > 0) {
                      return (
                        <BorrowerConditionsSection
                          title="At Closing"
                          conditions={filteredAc}
                          templates={templates}
                          uploads={uploads}
                          notes={conditionNotes}
                          onDownloadTemplate={handleDownloadConditionDoc}
                          onUploadClick={handleUploadClick}
                          borrowerPairs={loanData.borrowerPairs}
                        />
                      );
                    } else if (ownerFilter !== 'all') {
                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Circle className="h-5 w-5 text-gray-300" />
                            <h3 className="font-semibold text-gray-400">At Closing</h3>
                          </div>
                          <p className="text-sm text-muted-foreground pl-7">
                            No conditions assigned to {ownerFilter}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Empty state */}
                  {allConditions.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No conditions found for this loan.
                    </div>
                  )}

                  {/* All cleared state */}
                  {allConditions.length > 0 && openConditions.length === 0 && showOnlyOpen && (
                    <div className="text-center py-8">
                      <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="text-green-700 font-medium">All conditions have been cleared!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Toggle "Open only" off to view cleared conditions.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {selectedCondition && (
        <ConditionUploadModal
          open={uploadModalOpen}
          onOpenChange={setUploadModalOpen}
          condition={selectedCondition}
          token={token || ''}
          onSuccess={handleUploadSuccess}
          isFileLocked={loanData.isFileLocked}
          fileLockedByName={loanData.fileLockedByName}
        />
      )}

      {/* Title/Escrow Assignment Modal */}
      <TitleEscrowAssignModal
        open={titleEscrowModalOpen}
        onOpenChange={setTitleEscrowModalOpen}
        conditions={allConditions.filter(c => c.statusOpen)}
        token={token || ''}
        titleEscrowContact={loanData.titleEscrow}
        loanData={loanData}
        loanOfficer={loanOfficer}
        company={company}
        templates={templates}
        additionalCcRecipients={additionalCcRecipients}
        loanId={loanData.loanGuid}
        onSuccess={(portalUrl) => {
          toast({
            title: 'Title/Escrow Portal Created',
            description: 'A notification has been sent to the loan team.'
          });
          // Copy URL to clipboard
          navigator.clipboard.writeText(portalUrl).then(() => {
            toast({
              title: 'Link Copied',
              description: 'The portal link has been copied to your clipboard.'
            });
          });
        }}
      />

      {/* Bulk Reassign Modal */}
      <BulkReassignModal
        open={bulkReassignModalOpen}
        onOpenChange={setBulkReassignModalOpen}
        conditions={allConditions.filter(c => c.statusOpen)}
        token={token || ''}
        onSuccess={() => {
          toast({
            title: 'Conditions Reassigned',
            description: 'The conditions have been updated in Encompass.'
          });
          // Refresh data to show updated assignments
          fetchPortalData();
        }}
        isFileLocked={loanData.isFileLocked}
        fileLockedByName={loanData.fileLockedByName}
      />
    </div>
  );
};

/**
 * Loading skeleton
 */
const PortalLoadingState = () => (
  <div className="h-screen overflow-y-auto bg-background flex flex-col">
    <div className="p-4 md:p-6 border-b bg-card">
      <div className="max-w-7xl mx-auto flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-24 mt-1" />
        </div>
      </div>
    </div>
    <div className="flex-1 p-4 md:p-8">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
        <div className="lg:col-span-2">
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * Error state
 */
const PortalErrorState = ({
  error,
  errorCode,
  onRetry
}: {
  error: string;
  errorCode: string | null;
  onRetry?: () => void;
}) => {
  const getErrorIcon = () => {
    switch (errorCode) {
      case 'EXPIRED_TOKEN':
        return <Clock className="h-16 w-16 text-warning" />;
      case 'REVOKED_TOKEN':
        return <AlertCircle className="h-16 w-16 text-destructive" />;
      default:
        return <AlertCircle className="h-16 w-16 text-muted-foreground" />;
    }
  };

  const getErrorTitle = () => {
    switch (errorCode) {
      case 'EXPIRED_TOKEN':
        return 'Link Expired';
      case 'REVOKED_TOKEN':
        return 'Access Revoked';
      case 'INVALID_TOKEN':
        return 'Invalid Link';
      default:
        return 'Something Went Wrong';
    }
  };

  return (
    <div className="h-screen overflow-y-auto bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="mb-6">{getErrorIcon()}</div>
          <h1 className="text-2xl font-bold mb-2">{getErrorTitle()}</h1>
          <p className="text-muted-foreground mb-6">{error}</p>

          {errorCode === 'EXPIRED_TOKEN' && (
            <p className="text-sm text-muted-foreground mb-4">
              Please regenerate the link to continue.
            </p>
          )}

          <div className="flex justify-center gap-2">
            {onRetry && (
              <Button onClick={onRetry}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}

            <Button variant="outline" asChild>
              <a href="tel:877-448-5402">
                <Phone className="h-4 w-4 mr-2" />
                Call Support
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Team contact row component
 */
const TeamContactRow = ({
  label,
  contact
}: {
  label: string;
  contact: LoanTeamContact;
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="pl-6 space-y-1">
        <p className="font-medium text-sm">{contact.name}</p>
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#183C2E] transition-colors"
          >
            <Mail className="h-3 w-3" />
            <span className="truncate">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a
            href={`tel:${contact.phone.replace(/\D/g, '')}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#183C2E] transition-colors"
          >
            <Phone className="h-3 w-3" />
            <span>{contact.phone}</span>
          </a>
        )}
      </div>
    </div>
  );
};

/**
 * Title/Escrow contact row component
 */
const TitleEscrowContactRow = ({
  label,
  titleEscrow
}: {
  label: string;
  titleEscrow: TitleEscrowInfo;
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className="pl-6 space-y-1">
        <p className="font-medium text-sm">{titleEscrow.contactName}</p>
        {titleEscrow.email && (
          <a
            href={`mailto:${titleEscrow.email}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#183C2E] transition-colors"
          >
            <Mail className="h-3 w-3" />
            <span className="truncate">{titleEscrow.email}</span>
          </a>
        )}
        {titleEscrow.phone && (
          <a
            href={`tel:${titleEscrow.phone.replace(/\D/g, '')}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#183C2E] transition-colors"
          >
            <Phone className="h-3 w-3" />
            <span>{titleEscrow.phone}</span>
          </a>
        )}
      </div>
    </div>
  );
};

export default ConditionsPortal;
